import { useTheme } from '@emotion/react';

import type { SingleSlate } from '../types/single.slate';
import type { SinglePick } from '../types/single.pick';
import { PickVariant } from '../types/single.pick';

type ColorPickStatus = Pick<
  SingleSlate,
  'isActive' | 'isSelected' | 'isLoser' | 'isWinner' | 'isLive' | 'isEliminated'
> & { shouldMakePicks?: boolean };

type UseBGColorPickStatusProps = {
  picks?: SinglePick[];
  variant?: PickVariant;
} & ColorPickStatus;

const useBGColorPickStatus = ({
  picks = [],
  isWinner,
  isLoser,
  isSelected,
  isActive,
  variant,
  isLive,
  isEliminated,
  shouldMakePicks,
}: UseBGColorPickStatusProps) => {
  const theme = useTheme();

  if (shouldMakePicks) return 'transparent';
  if (isEliminated) return theme.colors.dark[5];
  if (isWinner) return theme.colors.green[2];
  if (isLoser) return theme.colors.red[1];
  if (isSelected || isLive) return theme.colors.splash[5];
  if (isActive) return theme.white;

  if (variant === PickVariant.MY_ENTRIES) return 'transparent';

  const isFilled = picks.some((pick) => pick?.teamId && pick?.logo && pick?.name);
  if (isFilled) return theme.colors.dark[5];

  return theme.colors.dark[7];
};

const useAnimationBGColorPickStatus = ({
  picks = [],
  isWinner,
  isLoser,
  isSelected,
  isActive,
  isLive,
  isEliminated,
}: UseBGColorPickStatusProps) => {
  const theme = useTheme();

  if (isEliminated) return theme.colors.dark[5];
  if (isWinner) return theme.colors.green[5];
  if (isLoser) return theme.colors.red[6];
  if (isSelected || isLive) return theme.colors.splash[7];
  if (isActive) return theme.colors.gray[4];

  const isFilled = picks.some((pick) => pick?.teamId && pick?.logo && pick?.name);
  if (isFilled) return theme.colors.dark[8];

  return theme.colors.dark[9];
};

type UseTextColorPickStatusProps = ColorPickStatus & {
  variant?: PickVariant;
};

const useTextColorPickStatus = ({
  isWinner,
  isLoser,
  isActive,
  isSelected,
  isLive,
  variant,
  isEliminated,
  shouldMakePicks,
}: UseTextColorPickStatusProps) => {
  const theme = useTheme();

  if (shouldMakePicks) return theme.colors.red[6];
  if (variant === PickVariant.MY_ENTRIES) return theme.colors.dark[6];

  if (isEliminated) return theme.colors.gray[6];
  if (isWinner || isLoser || isSelected || isActive || isLive) return theme.colors.gray[9];

  return theme.white;
};

const useBorderColorPickStatus = ({
  isWinner,
  isLoser,
  isSelected,
  isActive,
  isLive,
  isEliminated,
  shouldMakePicks,
}: ColorPickStatus) => {
  const theme = useTheme();

  if (shouldMakePicks) return theme.colors.red[6];
  if (isEliminated) return theme.colors.dark[5];
  if (isWinner) return theme.colors.green[5];
  if (isLoser) return theme.colors.red[8];
  if (isSelected || isLive) return theme.colors.splash[5];
  if (isActive) return theme.white;

  return theme.colors.gray[3];
};

export {
  useBorderColorPickStatus,
  useBGColorPickStatus,
  useTextColorPickStatus,
  useAnimationBGColorPickStatus,
};
